import {
  FC,
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useApi, useApiHolder } from '@backstage/core-plugin-api';
import {
  mgaApiRef,
  Conversation,
  ConversationAssistant,
  ChatMessage,
  ChatAgentRequest,
  ChatCompletionResponse,
  Message,
} from '../api/MgaApi';
import { defaultAssistantId } from '../constants';
import {
  RAG_ASSISTANT_ID,
  type RagAssistantAdapter,
  type RagConversation,
} from '../api/RagAssistantAdapter';
import {
  BACKSTAGE_AGENT_ASSISTANT_ID,
  type BackstageAgentAdapter,
  type BackstageAgentConversation,
} from '../api/BackstageAgentAdapter';
import {
  isBackstageAgentConversationId,
  isRagConversationId,
} from '../api/LocalAssistants';
import {
  AGENT_PROGRESS_MESSAGE_ID_SUFFIX,
  MessageRole,
  STORAGE_KEYS,
  TEMP_CONVERSATION_PREFIX,
} from '../api/constants';
import type { ChatAgentResponse } from '../api/MgaApi';

interface SendMessageOptions {
  forceNewConversation?: boolean;
  assistantId?: string;
  ephemeral?: boolean;
}

type LocalStoredConversation = RagConversation | BackstageAgentConversation;

interface LocalAssistantAdapter {
  conversationPrefix: string;
  assistantInfo: ConversationAssistant;
  getConversation(conversationId: string): LocalStoredConversation | null;
  sendMessage(
    request: ChatAgentRequest,
    options?: {
      onStream?: (response: ChatCompletionResponse) => void;
      ephemeral?: boolean;
    },
  ): Promise<ChatAgentResponse>;
  // Accepts the union so ChatContext can share one send path; each concrete
  // adapter is only ever called with conversations it itself produced.
  convertToMgaConversation(conversation: LocalStoredConversation): Conversation;
}

function toLocalAssistantAdapter(
  adapter: RagAssistantAdapter | BackstageAgentAdapter,
): LocalAssistantAdapter {
  return adapter as LocalAssistantAdapter;
}

interface ChatContextType {
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  activeConversation: {
    data?: Conversation;
    error?: Error;
    isLoading: boolean;
  };
  pendingAssistant: ConversationAssistant | null;
  prepareNewConversation: (assistant: ConversationAssistant) => void;
  sendMessage: (
    content: string,
    options?: SendMessageOptions,
  ) => Promise<string | null>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const createMessage = (
  role: typeof MessageRole.USER | typeof MessageRole.ASSISTANT,
  content: string,
  cost = 0,
): Message => ({
  id: `msg-${Date.now()}-${role}`,
  role,
  content,
  cost,
  creation_datetime: new Date().toISOString(),
});

const getLocalConversation = (conversationId: string): Conversation | null => {
  const localCache = localStorage.getItem(
    STORAGE_KEYS.localConversation(conversationId),
  );
  if (localCache) {
    try {
      return JSON.parse(localCache);
    } catch {
      // Remove corrupted localStorage data and return null
      localStorage.removeItem(STORAGE_KEYS.localConversation(conversationId));
    }
  }
  return null;
};

const saveLocalConversation = (
  conversationId: string,
  conversation: Conversation,
) => {
  localStorage.setItem(
    STORAGE_KEYS.localConversation(conversationId),
    JSON.stringify(conversation),
  );
};

const getConversationMappings = (): Record<string, string> => {
  try {
    return JSON.parse(
      localStorage.getItem(STORAGE_KEYS.CONVERSATION_MAPPINGS) || '{}',
    );
  } catch {
    // Return empty object on parse error - mappings will be rebuilt as needed
    return {};
  }
};

function createDefaultConversation(
  convoId: string,
  pendingAssistant: ConversationAssistant | null,
  assistantId: string,
  messages: Message[],
  content: string,
  cost: number,
): Conversation {
  const now = new Date().toISOString();

  const assistant = pendingAssistant
    ? {
        id: pendingAssistant.id,
        name: pendingAssistant.name,
        logo_url: pendingAssistant.logoUrl || '',
        description: pendingAssistant.description || '',
        model: {
          model: pendingAssistant.model || '',
          name: pendingAssistant.model || '',
        },
        tools: [],
        files: [],
      }
    : {
        id: assistantId,
        name: 'Assistant',
        logo_url: '',
        description: '',
        model: {
          model: '',
          name: '',
        },
        tools: [],
        files: [],
      };

  return {
    id: convoId,
    messages,
    assistant,
    name: content.substring(0, 50),
    modification_date: now,
    total_cost: cost,
    tools: [],
    files: [],
  };
}

const saveConversationMapping = (internalId: string, externalId: string) => {
  const mappings = getConversationMappings();
  mappings[internalId] = externalId;
  localStorage.setItem(
    STORAGE_KEYS.CONVERSATION_MAPPINGS,
    JSON.stringify(mappings),
  );
};

export const ChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const mgaApi = useApi(mgaApiRef);
  const apiHolder = useApiHolder();
  const wiredApiHolder = useRef<typeof apiHolder | null>(null);
  if (wiredApiHolder.current !== apiHolder) {
    mgaApi.setApiHolder?.(apiHolder);
    wiredApiHolder.current = apiHolder;
  }
  const { mutate } = useSWRConfig();
  const getLocalAdapterByAssistantId = useCallback(
    (assistantId: string): LocalAssistantAdapter | undefined => {
      if (assistantId === RAG_ASSISTANT_ID) {
        const adapter = mgaApi.getLocalAssistantAdapter?.(assistantId);
        return adapter
          ? toLocalAssistantAdapter(adapter as RagAssistantAdapter)
          : undefined;
      }
      if (assistantId === BACKSTAGE_AGENT_ASSISTANT_ID) {
        const adapter = mgaApi.getLocalAssistantAdapter?.(assistantId);
        return adapter
          ? toLocalAssistantAdapter(adapter as BackstageAgentAdapter)
          : undefined;
      }
      return undefined;
    },
    [mgaApi],
  );

  const getLocalAdapterByConversationId = useCallback(
    (conversationId: string): LocalAssistantAdapter | undefined => {
      if (isRagConversationId(conversationId)) {
        return getLocalAdapterByAssistantId(RAG_ASSISTANT_ID);
      }
      if (isBackstageAgentConversationId(conversationId)) {
        return getLocalAdapterByAssistantId(BACKSTAGE_AGENT_ASSISTANT_ID);
      }
      return undefined;
    },
    [getLocalAdapterByAssistantId],
  );

  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [pendingAssistant, setPendingAssistant] =
    useState<ConversationAssistant | null>({
      id: defaultAssistantId,
      name: 'Backstage Assistant',
      description: 'Loading assistant details...',
      logoUrl: '',
      model: '',
    });
  const isAssistantExplicitlySet = useRef(false);

  // manage conversation locally
  const swrKey = activeConversationId
    ? `conversations/${activeConversationId}`
    : null;

  const {
    data: activeConversationData,
    error,
    isLoading,
    mutate: mutateConversation,
  } = useSWR<Conversation | null>(
    swrKey,
    async () => {
      if (!activeConversationId) return null;

      const localAdapter =
        getLocalAdapterByConversationId(activeConversationId);
      if (localAdapter) {
        const localConversation =
          localAdapter.getConversation(activeConversationId);
        if (localConversation) {
          return localAdapter.convertToMgaConversation(localConversation);
        }
      }

      // check if local
      const localConversation = getLocalConversation(activeConversationId);
      if (localConversation) return localConversation;

      // if not a temp or local conversation and no local cache, fetch from backend
      if (
        !activeConversationId.startsWith(`${TEMP_CONVERSATION_PREFIX}-`) &&
        !getLocalAdapterByConversationId(activeConversationId)
      ) {
        return await mgaApi.getConversationById(activeConversationId);
      }

      return null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const { data: defaultAssistantData } = useSWR(
    `assistants/${defaultAssistantId}`,
    () => mgaApi.getAssistantById(defaultAssistantId),
    { revalidateOnFocus: false },
  );

  const prepareNewConversation = useCallback(
    (assistant: ConversationAssistant) => {
      setActiveConversationId(null);
      setPendingAssistant(assistant);
      isAssistantExplicitlySet.current = true;
    },
    [],
  );

  const buildChatAgentRequest = useCallback(
    (
      messages: ChatMessage[],
      assistantId: string,
      externalConversationId?: string,
    ): ChatAgentRequest => ({
      messages: messages.map(m => ({
        role: m.role as ChatAgentRequest['messages'][number]['role'],
        content: m.content,
      })),
      assistant_id: assistantId,
      conversation_id: externalConversationId,
    }),
    [],
  );

  const sendLocalAssistantMessage = useCallback(
    async (
      content: string,
      assistantId: string,
      conversation: Conversation | undefined,
      conversationId: string | null,
      localAdapter: LocalAssistantAdapter,
      options?: {
        ephemeral?: boolean;
      },
    ): Promise<string | null> => {
      let localConversationId = conversationId;
      if (!localConversationId) {
        localConversationId = `${
          localAdapter.conversationPrefix
        }-${Date.now()}`;
        setActiveConversationId(localConversationId);
      }

      const userMessage = createMessage(MessageRole.USER, content);
      const optimisticConversation: Conversation = conversation
        ? {
            ...conversation,
            messages: [...conversation.messages, userMessage],
            modification_date: new Date().toISOString(),
          }
        : {
            id: localConversationId,
            name:
              content.substring(0, 50) ||
              `${localAdapter.assistantInfo.name} Conversation`,
            messages: [userMessage],
            assistant: {
              id: localAdapter.assistantInfo.id,
              name: localAdapter.assistantInfo.name,
              description: localAdapter.assistantInfo.description || '',
              logo_url: localAdapter.assistantInfo.logoUrl || '',
              model: {
                model: localAdapter.assistantInfo.model || '',
                name: localAdapter.assistantInfo.name,
              },
              tools: [],
              files: [],
            },
            creation_date: new Date().toISOString(),
            modification_date: new Date().toISOString(),
            total_cost: 0,
            tools: [],
            files: [],
          };

      await mutate(
        `conversations/${localConversationId}`,
        optimisticConversation,
        false,
      );

      const messages: ChatMessage[] = conversation
        ? [
            ...conversation.messages.map(m => ({
              role: m.role,
              content: m.content,
            })),
            { role: MessageRole.USER, content },
          ]
        : [{ role: MessageRole.USER, content }];

      try {
        const chatAgentRequest = buildChatAgentRequest(
          messages,
          assistantId,
          localConversationId,
        );
        const progressMessageId = `msg-${localConversationId}${AGENT_PROGRESS_MESSAGE_ID_SUFFIX}`;
        const progressSteps: string[] = [];
        const agentResponse = await localAdapter.sendMessage(chatAgentRequest, {
          ephemeral: options?.ephemeral,
          onStream: streamResponse => {
            if (!streamResponse.content) {
              return;
            }

            progressSteps.push(streamResponse.content);

            const progressConversation: Conversation = {
              ...optimisticConversation,
              messages: [
                ...optimisticConversation.messages.filter(
                  message => message.id !== progressMessageId,
                ),
                {
                  id: progressMessageId,
                  role: MessageRole.ASSISTANT,
                  content: streamResponse.content,
                  agentProgress: [...progressSteps],
                  cost: 0,
                  creation_datetime: new Date().toISOString(),
                  conversation_id: localConversationId,
                  parent_id: null,
                },
              ],
              modification_date: new Date().toISOString(),
            };
            void mutate(
              `conversations/${localConversationId}`,
              progressConversation,
              false,
            );
          },
        });

        const updatedConversation =
          localAdapter.getConversation(localConversationId);
        if (updatedConversation) {
          await mutate(
            `conversations/${localConversationId}`,
            localAdapter.convertToMgaConversation(updatedConversation),
            false,
          );
        } else {
          const assistantContent =
            agentResponse.choices[0]?.message?.content ?? '';
          if (assistantContent) {
            await mutate(
              `conversations/${localConversationId}`,
              {
                ...optimisticConversation,
                messages: [
                  ...optimisticConversation.messages.filter(
                    message => message.id !== progressMessageId,
                  ),
                  createMessage(MessageRole.ASSISTANT, assistantContent),
                ],
                modification_date: new Date().toISOString(),
              },
              false,
            );
          }
        }

        if (!options?.ephemeral) {
          await mutate('conversations', undefined, { revalidate: true });
        }

        if (agentResponse.usage?.total_tokens) {
          const tokenKey = STORAGE_KEYS.tokenUsage(localConversationId);
          const existingTokens = parseInt(
            localStorage.getItem(tokenKey) || '0',
            10,
          );
          localStorage.setItem(
            tokenKey,
            String(existingTokens + agentResponse.usage.total_tokens),
          );
        }

        return agentResponse.conversation_id;
      } catch (err) {
        const updatedConversation =
          localAdapter.getConversation(localConversationId);
        if (updatedConversation) {
          await mutate(
            `conversations/${localConversationId}`,
            localAdapter.convertToMgaConversation(updatedConversation),
            false,
          );
        } else if (conversation) {
          await mutate(
            `conversations/${localConversationId}`,
            conversation,
            false,
          );
        } else {
          await mutate(`conversations/${localConversationId}`, null, false);
        }
        throw err;
      }
    },
    [buildChatAgentRequest, mutate, setActiveConversationId],
  );

  const sendMgaAssistantMessage = useCallback(
    async (
      content: string,
      assistantId: string,
      initialConversation: Conversation | undefined,
      initialConversationId: string | null,
      selectedPendingAssistant: ConversationAssistant | null,
    ): Promise<string | null> => {
      let conversation = initialConversation;
      let convoId = initialConversationId;
      let externalConversationId: string | undefined;

      if (!convoId) {
        try {
          const newConvo = await mgaApi.createConversation({
            title: content.substring(0, 50) || 'New Conversation',
            assistantId,
          });

          convoId = newConvo.id;
          setActiveConversationId(convoId);
          setPendingAssistant(null);

          // Create initial conversation with user message
          const userMessage = createMessage(MessageRole.USER, content);
          const createdConversation: Conversation = {
            ...newConvo,
            messages: [userMessage],
            // Override assistant if we have pendingAssistant with more complete info
            ...(selectedPendingAssistant && {
              assistant: {
                ...newConvo.assistant,
                name: selectedPendingAssistant.name,
                description: selectedPendingAssistant.description || '',
                logo_url: selectedPendingAssistant.logoUrl || '',
                model: selectedPendingAssistant.model
                  ? {
                      ...newConvo.assistant.model,
                      model: selectedPendingAssistant.model,
                    }
                  : newConvo.assistant.model,
              },
            }),
          };

          // Store locally and update cache
          saveLocalConversation(convoId, createdConversation);
          await mutate(`conversations/${convoId}`, createdConversation, false);
          conversation = createdConversation;
        } catch (err) {
          throw new Error('Failed to create conversation');
        }
      } else {
        // Add user message optimistically
        if (conversation) {
          const userMessage = createMessage(MessageRole.USER, content);
          const optimisticData: Conversation = {
            ...conversation,
            messages: [...conversation.messages, userMessage],
          };
          await mutate(`conversations/${convoId}`, optimisticData, false);
        }
      }

      // Build messages for API call
      const messages: ChatMessage[] = conversation
        ? [
            ...conversation.messages.map(m => ({
              role: m.role,
              content: m.content,
            })),
            { role: MessageRole.USER, content },
          ]
        : [{ role: MessageRole.USER, content }];

      // Get external conversation ID if it exists
      if (conversation && initialConversationId) {
        const mappings = getConversationMappings();
        externalConversationId = mappings[initialConversationId];
      }

      try {
        const chatAgentRequest = buildChatAgentRequest(
          messages,
          assistantId,
          externalConversationId,
        );
        const agentResponse = await mgaApi.chatAgent(chatAgentRequest);

        const response: ChatCompletionResponse = {
          content: agentResponse.choices[0]?.message?.content || '',
          model: agentResponse.model,
          conversationId: agentResponse.conversation_id,
          internalConversationId: undefined,
          usage: agentResponse.usage
            ? {
                totalTokens: agentResponse.usage.total_tokens,
                cost: agentResponse.cost,
              }
            : undefined,
        };

        let newConversationId: string | null = null;

        if (!convoId && response.conversationId) {
          newConversationId =
            response.internalConversationId ||
            `${TEMP_CONVERSATION_PREFIX}-${Date.now()}`;
          convoId = newConversationId;
          setActiveConversationId(newConversationId);
          setPendingAssistant(null);

          if (response.internalConversationId && response.conversationId) {
            saveConversationMapping(
              response.internalConversationId,
              response.conversationId,
            );
          }

          // revalidate
          await mutate('conversations', undefined, { revalidate: true });
        } else if (
          convoId &&
          response.internalConversationId &&
          response.internalConversationId !== convoId
        ) {
          if (response.conversationId) {
            saveConversationMapping(
              response.internalConversationId,
              response.conversationId,
            );
          }
        }

        // Update conversation with response
        if (convoId) {
          const assistantMessage = createMessage(
            MessageRole.ASSISTANT,
            response.content,
            response.usage?.cost || 0,
          );

          // Build message history, deduping if needed
          const currentMessages = conversation?.messages || [];
          const lastMessage = currentMessages[currentMessages.length - 1];
          const isLastMessageOurUserMessage =
            lastMessage?.role === MessageRole.USER &&
            lastMessage.content === content;

          const fullMessageHistory = isLastMessageOurUserMessage
            ? [...currentMessages, assistantMessage]
            : [
                ...currentMessages,
                createMessage(MessageRole.USER, content),
                assistantMessage,
              ];

          const newTotalCost =
            (conversation?.total_cost || 0) + (response.usage?.cost || 0);

          // update chat
          const updatedConversation: Conversation = conversation
            ? {
                ...conversation,
                messages: fullMessageHistory,
                modification_date: new Date().toISOString(),
                total_cost: newTotalCost,
              }
            : createDefaultConversation(
                convoId,
                selectedPendingAssistant,
                assistantId,
                fullMessageHistory,
                content,
                response.usage?.cost || 0,
              );

          saveLocalConversation(convoId, updatedConversation);

          // track usage
          if (response.usage?.totalTokens) {
            const tokenKey = STORAGE_KEYS.tokenUsage(convoId);
            const existingTokens = parseInt(
              localStorage.getItem(tokenKey) || '0',
              10,
            );
            localStorage.setItem(
              tokenKey,
              String(existingTokens + response.usage.totalTokens),
            );
          }

          await mutateConversation(updatedConversation, false);
        }

        return newConversationId;
      } catch (e) {
        // revert optimistic update on error
        if (convoId && conversation) {
          await mutate(`conversations/${convoId}`, conversation);
        }
        throw e;
      }
    },
    [
      mgaApi,
      mutate,
      mutateConversation,
      setActiveConversationId,
      buildChatAgentRequest,
    ],
  );

  const sendMessage = useCallback(
    async (
      content: string,
      options?: SendMessageOptions,
    ): Promise<string | null> => {
      const conversation =
        options?.forceNewConversation ||
        activeConversationData?.id !== activeConversationId
          ? undefined
          : activeConversationData;
      const convoId = options?.forceNewConversation
        ? null
        : activeConversationId;
      const assistantId =
        options?.assistantId ||
        conversation?.assistant?.id ||
        pendingAssistant?.id ||
        defaultAssistantId;

      if (!assistantId) {
        throw new Error('No assistant selected');
      }

      const localAdapter = getLocalAdapterByAssistantId(assistantId);
      if (localAdapter) {
        return sendLocalAssistantMessage(
          content,
          assistantId,
          conversation,
          convoId,
          localAdapter,
          { ephemeral: options?.ephemeral },
        );
      }

      return sendMgaAssistantMessage(
        content,
        assistantId,
        conversation,
        convoId,
        pendingAssistant,
      );
    },
    [
      activeConversationData,
      pendingAssistant,
      activeConversationId,
      getLocalAdapterByAssistantId,
      sendLocalAssistantMessage,
      sendMgaAssistantMessage,
    ],
  );

  useEffect(() => {
    if (
      !activeConversationId &&
      defaultAssistantData &&
      !isAssistantExplicitlySet.current
    ) {
      setPendingAssistant({
        id: defaultAssistantData.id,
        name: defaultAssistantData.name,
        description: defaultAssistantData.description,
        logoUrl: defaultAssistantData.logo_url,
        model: defaultAssistantData.model?.model,
      });
    }

    if (activeConversationId) {
      isAssistantExplicitlySet.current = false;
    }
  }, [activeConversationId, defaultAssistantData]);

  // Clear pendingAssistant once the active conversation is loaded
  useEffect(() => {
    if (
      activeConversationId &&
      activeConversationData &&
      !isLoading &&
      pendingAssistant
    ) {
      setPendingAssistant(null);
    }
  }, [activeConversationId, activeConversationData, isLoading, pendingAssistant]);

  const contextValue: ChatContextType = {
    activeConversationId,
    setActiveConversationId,
    activeConversation: {
      data: activeConversationData || undefined,
      error,
      isLoading,
    },
    pendingAssistant,
    prepareNewConversation,
    sendMessage,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
};
